/**
 * @file po-supplier-group.tsx
 * @module app/modules/uProcure/po-management/po-supplier-group
 * @description PO details: the supplier group the PO was made for (Draft PO groups) with the
 *   details copied when its draft was submitted: contact person, credit limit, TIN / VAT and bank
 *   accounts (the ones to share the PO with and pay). The ledger stays on the supplier.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { Card, Col, Row } from "react-bootstrap";
import { BANKTYPES } from "../../../../utils/staticJSON";

export interface PoSupplierGroup {
  name: string;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  creditLimit: number | null;
  tinNumber: string | null;
  vatNumber: string | null;
  banks: { bankName: string; branchName: string | null; accountNumber: string; identifierCode: string; bankType: number | null; tigoNumber: string | null }[];
  /** details that are the supplier's own (the group has none of its own) */
  fromSupplier?: string[];
  at?: string;
}

const Item = ({ label, value, supplier }: { label: string; value: string | null; supplier?: boolean }) => (
  <Row className="mb-3">
    <Col md={5} xs={4}>
      <label className="fs-16 fw-500 text-dark">{label}:</label>
    </Col>
    <Col md={7} xs={8}>
      <span className={`fs-16 ${value ? "fw-600 text-dark" : "text-gray-500"}`}>{value || "Not set"}</span>
      {supplier && <span className="badge badge-light ms-2 fs-12 fw-500">Same as supplier</span>}
    </Col>
  </Row>
);

const PoSupplierGroupCard = ({ group }: { group: PoSupplierGroup }) => {
  const sup = (k: string) => (group.fromSupplier ?? []).includes(k);
  return (
    <Card className="card-bordered mb-5" data-testid="po-supplier-group">
      <Card.Header className="bg-light align-items-center">
        <Card.Title className="fs-22 fw-bolder">Supplier group: {group.name}</Card.Title>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col md={6}>
            <Item label="Contact person" value={group.contactName} supplier={sup("contactName")} />
            <Item label="Phone" value={group.phone} supplier={sup("phone")} />
            <Item label="Email" value={group.email} supplier={sup("email")} />
            <Item label="Credit limit" value={group.creditLimit == null ? null : `TSh ${group.creditLimit.toLocaleString("en-US")}`} supplier={sup("creditLimit")} />
          </Col>
          <Col md={6}>
            <Item label="TIN" value={group.tinNumber} supplier={sup("tinNumber")} />
            <Item label="VAT" value={group.vatNumber} supplier={sup("vatNumber")} />
            <Row className="mb-3">
              <Col md={5} xs={4}>
                <label className="fs-16 fw-500 text-dark">Bank accounts:</label>
              </Col>
              <Col md={7} xs={8}>
                {group.banks.length ? (
                  group.banks.map((b) => (
                    <div key={`${b.bankName}-${b.accountNumber}`} className="fs-16 fw-600 text-dark">
                      {[b.bankName, b.accountNumber, b.branchName, b.bankType ? `${BANKTYPES.find((t) => t.value === b.bankType)?.label ?? ""} ${b.tigoNumber ?? ""}`.trim() : ""]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  ))
                ) : (
                  <span className="text-gray-500 fs-16">Not set</span>
                )}
                {sup("banks") && <span className="badge badge-light fs-12 fw-500">Same as supplier</span>}
              </Col>
            </Row>
          </Col>
        </Row>
        <div className="fs-13 text-gray-600">As when the draft was submitted. The ledger and payments stay on the supplier.</div>
      </Card.Body>
    </Card>
  );
};

export default PoSupplierGroupCard;
